import { useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ArrowRightIcon from "@atlaskit/icon/core/arrow-right";
import StatusWarningIcon from "@atlaskit/icon/core/status-warning";
import { token } from "@atlaskit/tokens";

import AccountCreation, { Social } from "@/components/signup/account-creation";
import { fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

import { InputSubmitButton } from "./input-submit-button";

const containerCss = css({
    display: "flex",
    justifyContent: "space-between",
    gap: "32px",
    margin: "0 auto",
    width: "100%",
    maxWidth: "60.5rem"
});

const paragraphCss = css({
    marginBlockStart: "0.75rem",
    marginBlockEnd: "0.75rem"
});

const messageCss = css({
    width: "35.063rem"
});

const messageHeaderCss = css({
    fontFamily: token("font.family.brand.heading"), // Charlie Display
    fontWeight: 700,
    fontSize: "2rem",
    lineHeight: "2.375rem",
    letterSpacing: "0.019rem",
    color: wacToken("color.neutral.light1000")
});

const messageBodyCss = css({
    fontFamily: token("font.family.brand.body"), // Charlie Text
    fontSize: "1.25rem",
    lineHeight: "1.75rem",
    color: wacToken("color.neutral.light1000")
});

const errorCss = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "2px solid #ffaa00",
    // eslint-disable-next-line local/ensure-wac-token-usage
    background: "#f8f4ec",
    fontFamily: token("font.family.brand.body"), // Charlie Text
    fontSize: "0.875rem",
    lineHeight: "1.25",
    padding: "1rem",
    color: wacToken("color.primary.black"),
    display: "flex",
    gap: "1rem",
    alignItems: "center",
    borderRadius: "0.5rem"
});

const messageCtaCss = css({
    fontFamily: token("font.family.brand.body"), // Charlie Text
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: "1.25rem",
    color: wacToken("color.extended.blue700"),
    cursor: "pointer"
});

const accountCreationCss = css({
    width: "23rem",
    minWidth: "15rem"
});

const separatorBorderCss = css({
    borderColor: "inherit",
    borderStyle: "solid",
    borderWidth: 0.125,
    display: "block",
    flexGrow: 1,
    height: 0,
    margin: "0 1rem",
    opacity: 0.25
});

const separatorCss = css({
    fontSize: fontSize.sm,
    display: "flex",
    lineHeight: ".25px",
    margin: "2rem 0",
    textAlign: "center"
});

export const Header = ({ onRetryClick }: { onRetryClick?: () => void }) => {
    const {
        firePaidJiraAi,
        promptAttemptIndex,
        isErrorFromCgs,
        hasSubmittedDefaultValues
    } = useJiraAiFFL();
    const messages = {
        yourJiraSpaceIsReady: "Your Jira space is ready",
        getStartedNow:
            "Get started now to keep editing, save your progress, and make it your own.",
        tryAnotherPrompt: "Try another prompt"
    };

    useEffect(() => {
        firePaidJiraAi("AccountCreationViewed", "viewed", {
            promptAttemptIndex
        });
    }, [firePaidJiraAi, promptAttemptIndex]);

    return (
        <div css={containerCss}>
            <div css={messageCss}>
                <h2 css={messageHeaderCss}>{messages.yourJiraSpaceIsReady}</h2>
                <p css={[messageBodyCss, paragraphCss]}>
                    {messages.getStartedNow}
                </p>
                {/*On error, default results are shown that correspond to the default values*/}
                {isErrorFromCgs && !hasSubmittedDefaultValues && (
                    <div css={errorCss}>
                        <StatusWarningIcon
                            label="error"
                            color={token("color.icon.warning")}
                        />
                        <div>
                            We couldn&apos;t build the project the way you asked
                            for it. So we have built out a project that
                            showcases what Rovo and Jira are capable of.
                        </div>
                    </div>
                )}
                <p css={paragraphCss}>
                    <span
                        css={messageCtaCss}
                        onClick={() => onRetryClick?.()}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onRetryClick?.();
                            }
                        }}
                        aria-label={messages.tryAnotherPrompt}
                    >
                        {messages.tryAnotherPrompt}{" "}
                        <ArrowRightIcon
                            label={messages.tryAnotherPrompt}
                            size="small"
                        />
                    </span>
                </p>
            </div>
            <div css={accountCreationCss}>
                <AccountCreation
                    templateId="cfind-4666-jira-ai-preview"
                    Input={<InputSubmitButton />}
                    Submit={null}
                    Login={null}
                    Separator={<Separator />}
                    Social={<Social providers={{ google: "Google" }} />}
                />
            </div>
        </div>
    );
};

const Separator = () => {
    const intl = useIntl();

    const separatorText = intl.formatMessage({
        id: "WacSignupForm.socialSignUpOrSignUpWith",
        defaultMessage: "Or sign up with"
    });

    return (
        <div css={separatorCss}>
            <div css={separatorBorderCss} />
            {separatorText}
            <div css={separatorBorderCss} />
        </div>
    );
};
